import { View3dElement, TransformVector } from '@ikea-aoa/component-view-3d';
import { localeService } from '../../../services';
import { MeasurementUtility } from '@inter-ikea-kompis/utilities';
import {
	lineComponentIds,
	textComponentId,
	measurementId,
	defaultConfig,
} from './MeasurementsManager/Constants';
import {
	TransformPosition,
	LineRenderer,
	Component,
	Text,
	Node,
	Camera,
} from '@ikea-aoa/elements-3d';
import { Color } from '@ikea-aoa/elements-3d/lib/interfaces';
import { LineConfiguration } from '@ikea-aoa/elements-3d';
import { TextConfiguration } from '@ikea-aoa/elements-3d';
import { DeviceType, WriteDirection } from 'sofa-reborn-react';
import { store } from '../../../store';

const LINES_COUNT = 8;

interface MeasurementText {
	width: string;
	depth: string;
	height: string;
}

interface MeasurementsLocalization {
	measurementUnit: string;
	measurementFoldedOut: string;
}

interface MeasurementLine {
	line: LineRenderer;
	decorMax: LineRenderer;
	decorMin: LineRenderer;
	extended: LineRenderer;
	extendedDecorMax: LineRenderer;
	extendedDecorMin: LineRenderer;
	text: Text;
}

interface PositionExtends {
	[key: string]: {
		min: TransformPosition;
		max: TransformPosition;
	};
}

export interface MeasurementsStyleConfig {
	line: MeasurementsLineConfiguration;
	exLine: MeasurementsLineConfiguration;
	text: TextConfiguration;
	height: number;
	linesOffset: number;
	decorLength: number;
	textOffset: number;
	isHidden: boolean;
}

export interface MeasurementsLineConfiguration extends LineConfiguration {
	color: Color;
}

type Props = {
	useMetricMeasures: boolean | null;
	deviceType: DeviceType;
	writeDirection?: WriteDirection;
};

export class MeasurementsManager {
	private measurements = new Map<
		string,
		{ lines: Array<MeasurementLine>; config: MeasurementsStyleConfig }
	>();
	constructor(
		private view3d: View3dElement,
		private props: Props,
		public measurementText: MeasurementText = { width: '', depth: '', height: '' }
	) {}

	public updateProps(props: Props): void {
		this.props = props;
	}

	public create(id = measurementId, config = defaultConfig): void {
		if (this.measurements.has(id)) {
			this.destroy(id);
		}
		this.measurements.set(id, { lines: [], config });
	}

	public update(
		positionExtends: PositionExtends,
		fixedHeightValue: string | null,
		id = measurementId
	): void {
		const node = this.getNode(id);
		if (!node) {
			// nothing to update
			return;
		}
		if (!positionExtends['default'].max.x) {
			this.view3d.destroyObject(node);
			return;
		}

		const measurement = this.measurements.get(id);
		if (measurement && measurement.lines.length > 0 && measurement.config) {
			const lines = measurement.lines;
			const config = measurement.config;

			this.calculate(lines[0], config, positionExtends, 'z', 'max');
			this.calculate(lines[1], config, positionExtends, 'x', 'min');
			this.calculate(lines[2], config, positionExtends, 'z', 'min');
			this.calculate(lines[3], config, positionExtends, 'x', 'max');

			this.updateHeightLines(lines, config, positionExtends, fixedHeightValue);
		}
	}

	private updateHeightLines(
		lines: MeasurementLine[],
		config: MeasurementsStyleConfig,
		positionExtends: PositionExtends,
		fixedHeightValue: string | null
	): void {
		const line1 = lines[4];
		const line2 = lines[5];
		const line3 = lines[6];
		const line4 = lines[7];

		const ext = positionExtends.extended;
		const lineOffset = config.linesOffset;

		ext.max.x = ext.max.x ? ext.max.x : 0;
		ext.max.y = ext.max.y ? ext.max.y : 0;
		ext.max.z = ext.max.z ? ext.max.z : 0;
		ext.min.x = ext.min.x ? ext.min.x : 0;
		ext.min.y = ext.min.y ? ext.min.y : 0;
		ext.min.z = ext.min.z ? ext.min.z : 0;

		// Set the heigt lines position and length
		line1.line.state.points = [
			{ x: ext.max.x + lineOffset, y: 0, z: ext.min.z - lineOffset },
			{ x: ext.max.x + lineOffset, y: ext.max.y - ext.min.y, z: ext.min.z - lineOffset },
		];

		line2.line.state.points = [
			{ z: ext.min.z - lineOffset, y: 0, x: ext.min.x - lineOffset },
			{ z: ext.min.z - lineOffset, y: ext.max.y - ext.min.y, x: ext.min.x - lineOffset },
		];

		line3.line.state.points = [
			{ z: ext.max.z + lineOffset, y: 0, x: ext.min.x - lineOffset },
			{ z: ext.max.z + lineOffset, y: ext.max.y - ext.min.y, x: ext.min.x - lineOffset },
		];

		line4.line.state.points = [
			{ x: ext.max.x + lineOffset, y: 0, z: ext.max.z + lineOffset },
			{ x: ext.max.x + lineOffset, y: ext.max.y - ext.min.y, z: ext.max.z + lineOffset },
		];

		Object.assign(line1.line.state, config.line);
		Object.assign(line2.line.state, config.line);
		Object.assign(line3.line.state, config.line);
		Object.assign(line4.line.state, config.line);

		// Set the decor for the above height lines
		line1.decorMin.state.points = [
			{ ...line1.line.state.points[0], x: ext.max.x + lineOffset + config.decorLength },
			{ ...line1.line.state.points[0], x: ext.max.x + lineOffset - config.decorLength },
		];

		line1.decorMax.state.points = [
			{ ...line1.line.state.points[1], x: ext.max.x + lineOffset + config.decorLength },
			{ ...line1.line.state.points[1], x: ext.max.x + lineOffset - config.decorLength },
		];

		line2.decorMin.state.points = [
			{ ...line2.line.state.points[0], z: ext.min.z - lineOffset + config.decorLength },
			{ ...line2.line.state.points[0], z: ext.min.z - lineOffset - config.decorLength },
		];

		line2.decorMax.state.points = [
			{ ...line2.line.state.points[1], z: ext.min.z - lineOffset + config.decorLength },
			{ ...line2.line.state.points[1], z: ext.min.z - lineOffset - config.decorLength },
		];

		line3.decorMin.state.points = [
			{ ...line3.line.state.points[0], x: ext.min.x - lineOffset + config.decorLength },
			{ ...line3.line.state.points[0], x: ext.min.x - lineOffset - config.decorLength },
		];

		line3.decorMax.state.points = [
			{ ...line3.line.state.points[1], x: ext.min.x - lineOffset + config.decorLength },
			{ ...line3.line.state.points[1], x: ext.min.x - lineOffset - config.decorLength },
		];

		line4.decorMin.state.points = [
			{ ...line4.line.state.points[0], z: ext.max.z + lineOffset + config.decorLength },
			{ ...line4.line.state.points[0], z: ext.max.z + lineOffset - config.decorLength },
		];

		line4.decorMax.state.points = [
			{ ...line4.line.state.points[1], z: ext.max.z + lineOffset + config.decorLength },
			{ ...line4.line.state.points[1], z: ext.max.z + lineOffset - config.decorLength },
		];

		Object.assign(line1.decorMin.state, config.line);
		Object.assign(line1.decorMax.state, config.line);
		Object.assign(line2.decorMin.state, config.line);
		Object.assign(line2.decorMax.state, config.line);
		Object.assign(line3.decorMin.state, config.line);
		Object.assign(line3.decorMax.state, config.line);
		Object.assign(line4.decorMin.state, config.line);
		Object.assign(line4.decorMax.state, config.line);

		// Set the text for above height lines
		const height = fixedHeightValue
			? fixedHeightValue
			: this.getMeasurementsValue(ext.max.y, ext.min.y);
		const width = this.getMeasurementsValue(ext.max.x, ext.min.x);
		const depth = this.getMeasurementsValue(ext.max.z, ext.min.z);

		if (line1.text) {
			Object.assign(line1.text.state, config.text);
			line1.text.state.position = {
				...line1.line.state.points[0],
				x: ext.max.x + config.textOffset,
				y: (ext.max.y - ext.min.y) / 2,
			};
			line1.text.state.text = this.getMeasurementsText(height);
		}

		if (line2.text) {
			Object.assign(line2.text.state, config.text);
			line2.text.state.position = {
				...line2.line.state.points[0],
				z: ext.min.z - config.textOffset,
				y: (ext.max.y - ext.min.y) / 2,
			};
			line2.text.state.text = this.getMeasurementsText(height);
		}

		if (line3.text) {
			Object.assign(line3.text.state, config.text);
			line3.text.state.position = {
				...line3.line.state.points[0],
				x: ext.min.x - config.textOffset,
				y: (ext.max.y - ext.min.y) / 2,
			};
			line3.text.state.text = this.getMeasurementsText(height);
		}

		if (line4.text) {
			Object.assign(line4.text.state, config.text);
			line4.text.state.position = {
				...line4.line.state.points[0],
				z: ext.max.z + config.textOffset,
				y: (ext.max.y - ext.min.y) / 2,
			};
			line4.text.state.text = this.getMeasurementsText(height);
		}

		// Update height measurement for mobile/save to profile
		this.measurementText.height = this.getMeasurementsText(height);
		// Update width measurement for mobile/save to profile
		this.measurementText.width = this.getMeasurementsText(width);
		// Update height measurement for mobile/save to profile
		this.measurementText.depth = this.getMeasurementsText(depth);
	}

	private calculate(
		measurementLine: MeasurementLine,
		config: MeasurementsStyleConfig,
		positionExtends: PositionExtends,
		axis: 'x' | 'z',
		limit: 'max' | 'min'
	): void {
		const { line, decorMax, decorMin, extended, extendedDecorMax, extendedDecorMin, text } =
			measurementLine;
		const { height: y, decorLength: d } = config;
		const o = limit == 'max' ? config.linesOffset : -config.linesOffset;
		const t = limit == 'max' ? config.textOffset : -config.textOffset;
		const def = positionExtends.default;
		const ext = positionExtends.extended;
		const oAxis = this.getOpositeAxis(axis);
		const oValue = ext[limit][oAxis] as number;
		const extThreshold = 0.2;

		line.state.points = [
			{ [oAxis]: oValue + o, y, [axis]: def.max[axis] },
			{ [oAxis]: oValue + o, y, [axis]: def.min[axis] },
		];
		Object.assign(line.state, config.line);

		decorMax.state.points = [
			{ [oAxis]: oValue + o + d, y, [axis]: def.max[axis] },
			{ [oAxis]: oValue + o - d, y, [axis]: def.max[axis] },
		];
		Object.assign(decorMax.state, config.line);

		decorMin.state.points = [
			{ [oAxis]: oValue + o + d, y, [axis]: def.min[axis] },
			{ [oAxis]: oValue + o - d, y, [axis]: def.min[axis] },
		];
		Object.assign(decorMin.state, config.line);

		let extendedValue: string | null = null;
		let exLimit: string | null = null;
		let exMinMaxLimit: string | null = null;
		if (
			Math.abs(ext.max[axis as string] - def.max[axis as string]) > extThreshold &&
			Math.abs(ext.min[axis as string] - def.min[axis as string]) > extThreshold
		) {
			exMinMaxLimit = 'both';
		} else if (Math.abs(ext.max[axis as string] - def.max[axis as string]) > extThreshold) {
			exLimit = 'max';
		} else if (Math.abs(ext.min[axis as string] - def.min[axis as string]) > extThreshold) {
			exLimit = 'min';
		}

		/* Two extended modules on same axis */
		if (exMinMaxLimit) {
			extendedValue = this.getMeasurementsValue(ext.max[axis as string], ext.min[axis as string]);
			extended.state.points = [
				{ [oAxis]: oValue + o, y, [axis]: ext.min[axis] },
				{ [oAxis]: oValue + o, y, [axis]: ext.max[axis] },
			];
			Object.assign(extended.state, config.exLine);
			const dashNbBoth = Math.round(
				Math.abs(ext.max[axis as string] - ext.min[axis as string]) * 2
			);
			extended.state.dashNb = dashNbBoth;
			extendedDecorMax.state.points = [
				{ [oAxis]: oValue + o + d, y, [axis]: ext.max[axis] },
				{ [oAxis]: oValue + o - d, y, [axis]: ext.max[axis] },
			];
			Object.assign(extendedDecorMax.state, config.line);
			extendedDecorMin.state.points = [
				{ [oAxis]: oValue + o + d, y, [axis]: ext.min[axis] },
				{ [oAxis]: oValue + o - d, y, [axis]: ext.min[axis] },
			];
			Object.assign(extendedDecorMin.state, config.line);
		} else if (exLimit) {
			extendedValue = this.getMeasurementsValue(ext.max[axis as string], ext.min[axis as string]);
			extended.state.points = [
				{ [oAxis]: oValue + o, y, [axis]: def[exLimit][axis] },
				{ [oAxis]: oValue + o, y, [axis]: ext[exLimit][axis] },
			];
			Object.assign(extended.state, config.exLine);
			const dashNb = Math.round(Math.abs(ext[exLimit][axis] - def[exLimit][axis]) * 2);
			extended.state.dashNb = dashNb;
			if (exLimit === 'max') {
				extendedDecorMax.state.points = [
					{ [oAxis]: oValue + o + d, y, [axis]: ext[exLimit][axis] },
					{ [oAxis]: oValue + o - d, y, [axis]: ext[exLimit][axis] },
				];
				Object.assign(extendedDecorMax.state, config.line);
				if (extendedDecorMin.state.points.length < 1) {
					extendedDecorMin.state.points = [];
				}
			} else {
				extendedDecorMin.state.points = [
					{ [oAxis]: oValue + o + d, y, [axis]: ext[exLimit][axis] },
					{ [oAxis]: oValue + o - d, y, [axis]: ext[exLimit][axis] },
				];
				Object.assign(extendedDecorMin.state, config.line);
				if (extendedDecorMax.state.points.length < 1) {
					extendedDecorMax.state.points = [];
				}
			}
		} else {
			extended.state.points = [];
			extendedDecorMax.state.points = [];
			extendedDecorMin.state.points = [];
		}

		const value = this.getMeasurementsValue(def.max[axis as string], def.min[axis as string]);
		if (text) {
			Object.assign(text.state, config.text);
			text.state.position = {
				[oAxis]: oValue + t,
				y,
				[axis]: (ext.max[axis as string] + ext.min[axis]) / 2,
			};
			text.state.text = this.getMeasurementsText(value, extendedValue ? extendedValue : null);
		} else {
			if (axis === 'x') {
				this.measurementText.depth = this.getMeasurementsText(
					value,
					extendedValue ? extendedValue : null,
					true
				);
			} else if (axis === 'z') {
				this.measurementText.width = this.getMeasurementsText(
					value,
					extendedValue ? extendedValue : null,
					true
				);
			}
		}
	}

	public getMobileMeasurements(): string {
		if (this.props.writeDirection === 'rtl') {
			return (
				this.measurementText.height +
				' × ' +
				this.measurementText.width +
				' × ' +
				this.measurementText.depth
			);
		} else {
			return (
				this.measurementText.depth +
				' × ' +
				this.measurementText.width +
				' × ' +
				this.measurementText.height
			);
		}
	}

	public show(camera: Camera, id = measurementId): void {
		const node = this.view3d.getObject(id);
		if (!node) {
			// Nothing to show.
			return;
		}

		const measurement = this.measurements.get(id);
		if (measurement && measurement.lines.length > 0 && measurement.config) {
			const lines = measurement.lines;
			const config = measurement.config;
			config.isHidden = false;

			const cameraPosition = camera.getPosition();

			const widthLines = [lines[1], lines[3]];
			const depthLines = [lines[0], lines[2]];
			const heightLines = [lines[4], lines[5], lines[6], lines[7]];

			const width = this.getClosest(widthLines, cameraPosition);
			const depth = this.getClosest(depthLines, cameraPosition);
			const height = this.getHeightLineToShow(heightLines, cameraPosition);

			lines.forEach((line) => {
				if (line == width || line == depth || line == height) {
					this.enable(line);
				} else {
					this.disable(line);
				}
			});
		}
	}

	public hide(id = measurementId): void {
		const node = this.view3d.getObject(id);
		if (!node) {
			// Nothing to hide.
			return;
		}

		const measurement = this.measurements.get(id);
		if (measurement && measurement.lines.length > 0 && measurement.config) {
			const lines = measurement.lines;
			const config = measurement.config;
			config.isHidden = true;
			lines.forEach((line) => {
				this.disable(line);
			});
		}
	}

	public destroy(id: string): void {
		this.view3d.destroyObjectById(id);
		this.measurements.delete(id);
	}

	private disable(line: MeasurementLine): void {
		Object.values(line).forEach((c: Component) => (c.isDisabled = true));
	}

	private enable(line: MeasurementLine): void {
		Object.values(line).forEach((c: Component) => (c.isDisabled = false));
	}

	private getOpositeAxis(axis: 'x' | 'z'): 'x' | 'z' {
		if (axis == 'x') {
			return 'z';
		}
		return 'x';
	}
	private initLines(node: Node): Array<MeasurementLine> {
		const measurement = new Array<MeasurementLine>();
		for (let i = 0; i < LINES_COUNT; i++) {
			const line = new Object() as MeasurementLine;
			Object.entries(lineComponentIds).forEach(([k, v]) => {
				const c = node.addComponent(LineRenderer, v + i);
				line[k] = c;
			});
			if (this.props.deviceType !== 'mobile') {
				const c = node.addComponent(Text, textComponentId + i);
				line.text = c;
			}
			measurement.push(line);
		}
		return measurement;
	}

	private getClosest(
		lines: Array<MeasurementLine>,
		position: TransformPosition
	): MeasurementLine | null {
		let distance = Number.MAX_VALUE;
		let closest: MeasurementLine | null = null;
		lines.forEach((mLine) => {
			mLine.line.state.points.forEach((p) => {
				const d = TransformVector.distanceSquared(position, p);
				if (d < distance) {
					distance = d;
					closest = mLine;
				}
			});
		});
		return closest;
	}

	private getHeightLineToShow(
		lines: Array<MeasurementLine>,
		position: TransformPosition
	): MeasurementLine | null {
		// The closest height line will always be the on in the middle of the width and depth lines
		const closestLine = this.getClosest(lines, position);
		let heightLineToShow: MeasurementLine | null = null;

		// We want the line that is at the left of the closest height line
		switch (closestLine?.line.name) {
			case 'Line-4':
				const line5 = lines.find((line) => line.line.name == 'Line-5');
				heightLineToShow = line5 ? line5 : heightLineToShow;
				break;
			case 'Line-5':
				const line6 = lines.find((line) => line.line.name == 'Line-6');
				heightLineToShow = line6 ? line6 : heightLineToShow;
				break;
			case 'Line-6':
				const line7 = lines.find((line) => line.line.name == 'Line-7');
				heightLineToShow = line7 ? line7 : heightLineToShow;
				break;
			case 'Line-7':
				const line4 = lines.find((line) => line.line.name == 'Line-4');
				heightLineToShow = line4 ? line4 : heightLineToShow;
				break;
			default:
				heightLineToShow = null;
				break;
		}
		return heightLineToShow;
	}

	private initNode(id: string): Node {
		const node = this.view3d.createObject(id, TransformVector.zero());
		const lines = this.initLines(node);
		const measurement = this.measurements.get(id);
		if (measurement) {
			measurement.lines = lines;
		}
		return node;
	}

	private getNode(id: string): Node | false {
		const node = this.view3d.getObject(id);
		if (!node) {
			// Create node if possible
			if (this.measurements.has(id)) {
				return this.initNode(id);
			} else {
				// Can't create node.
				return false;
			}
		}
		return node;
	}
	private getMeasurementsValue(max: number, min: number): string {
		const result = Math.round((max - min) * 10);
		const translations = store.getState().localization.kompisTranslations?.kompis;
		if (this.props.useMetricMeasures) {
			return String(result);
		} else if (translations) {
			return MeasurementUtility.metricToImperial(translations, result * 10);
		}
		return '';
	}

	private getMeasurementsText(value: string, extenedValue?: string | null, mobile = false): string {
		const { measurementUnit: unit, measurementFoldedOut: extended } = this.getLocalization();
		if (this.props.writeDirection === 'rtl') {
			if (extenedValue) {
				if (!mobile) {
					return (
						unit.trim() +
						String.fromCharCode(parseInt('200E', 16)) +
						value +
						'\n' +
						extended +
						' ' +
						extenedValue +
						unit
					);
				} else {
					return (
						unit.trim() +
						String.fromCharCode(parseInt('200E', 16)) +
						' ' +
						extenedValue +
						' / ' +
						value
					);
				}
			}
			return unit.trim() + String.fromCharCode(parseInt('200E', 16)) + ' ' + value;
		}
		if (extenedValue) {
			if (!mobile) {
				return value + unit + '\n' + extended + ' ' + extenedValue + unit;
			} else {
				return value + ' / ' + extenedValue + unit;
			}
		}
		return value + unit;
	}

	private getLocalization(): MeasurementsLocalization {
		return {
			measurementUnit: localeService.getTranslation('measurementUnit'),
			measurementFoldedOut: localeService.getTranslation('measurementFoldedOut'),
		};
	}
}
